// @ts-check
import { Layers, Plus, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useToast } from '../context/ToastContext';
import api from '../utils/apiClient';

const MAIN_PIECE_KEY = 'main';

/** A single Shell/Lining composition dropdown pair. */
const CompositionFields = ({ compositions, shellId, liningId, onShellChange, onLiningChange }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
    <div>
      <label className="block text-sm font-medium mb-1">Shell Composition</label>
      {compositions.length > 0 ? (
        <select
          value={shellId || ''}
          onChange={(e) => onShellChange(e.target.value)}
          className="w-full px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground"
        >
          <option value="">Select Shell Composition</option>
          {compositions
            .filter((c) => c.shell_type === 'shell')
            .map((comp) => (
              <option key={comp.id} value={comp.id}>
                {comp.alias} ({comp.name})
              </option>
            ))}
        </select>
      ) : (
        <div className="text-sm text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/50 border border-amber-200 dark:border-amber-900 rounded-lg px-3 py-2">
          No compositions configured. Contact admin.
        </div>
      )}
    </div>

    <div>
      <label className="block text-sm font-medium mb-1">
        Lining Composition <span className="text-muted-foreground/70 font-normal">(Optional)</span>
      </label>
      {compositions.length > 0 ? (
        <select
          value={liningId || ''}
          onChange={(e) => onLiningChange(e.target.value)}
          className="w-full px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground"
        >
          <option value="">Select Lining Composition</option>
          {compositions
            .filter((c) => c.shell_type === 'lining')
            .map((comp) => (
              <option key={comp.id} value={comp.id}>
                {comp.alias} ({comp.name})
              </option>
            ))}
        </select>
      ) : (
        <div className="text-sm text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/50 border border-amber-200 dark:border-amber-900 rounded-lg px-3 py-2">
          No compositions configured. Contact admin.
        </div>
      )}
    </div>
  </div>
);

const ProductCompositionManager = ({
  productId,
  additionalPieceTypeIds,
  onAdditionalPieceTypeIdsChange,
  mainShellCompositionId,
  mainLiningCompositionId,
  handleChange,
}) => {
  const toast = useToast();
  const [compositions, setCompositions] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [pieceCompositions, setPieceCompositions] = useState(
    /** @type {Record<string, {shell_composition_id: string|null, lining_composition_id: string|null}>} */ ({}),
  );
  const [activePiece, setActivePiece] = useState(MAIN_PIECE_KEY);
  const [loading, setLoading] = useState(false);
  const [addingPiece, setAddingPiece] = useState(false);
  const [newPieceTypeId, setNewPieceTypeId] = useState('');

  const pieceTypeIds = additionalPieceTypeIds || [];

  const pieces = [
    { key: MAIN_PIECE_KEY, label: 'Main Garment' },
    ...pieceTypeIds.map((id) => ({
      key: id,
      label: (productTypes || []).find((pt) => pt.id === id)?.name || 'Piece',
    })),
  ];

  const availablePieceTypes = (productTypes || []).filter((pt) => !pieceTypeIds.includes(pt.id));

  const loadCompositions = useCallback(async () => {
    try {
      const data = await api.get('/api/admin/catalog/compositions');
      setCompositions(data || []);
    } catch {
      // Non-critical
    }
  }, []);

  const loadProductTypes = useCallback(async () => {
    try {
      const data = await api.get('/api/admin/taxonomy/product-types');
      setProductTypes((data || []).filter((t) => t.is_active));
    } catch {
      // Non-critical
    }
  }, []);

  const loadPieceCompositions = useCallback(async () => {
    if (!productId) return;
    setLoading(true);
    try {
      const data = await api.get(`/api/admin/products/${productId}/piece-compositions`);
      const byPiece =
        /** @type {Record<string, { shell_composition_id: string, lining_composition_id: string }>} */ ({});
      for (const item of data || []) {
        byPiece[item.piece_type_id] = {
          shell_composition_id: item.shell_composition_id,
          lining_composition_id: item.lining_composition_id,
        };
      }
      setPieceCompositions(byPiece);
    } catch {
      // None yet — that's fine
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    loadCompositions();
    loadProductTypes();
    loadPieceCompositions();
  }, [loadCompositions, loadProductTypes, loadPieceCompositions]);

  const persistPieceTypeIds = async (ids) => {
    await api.put(`/api/admin/products/${productId}`, { additional_piece_type_ids: ids });
    onAdditionalPieceTypeIdsChange?.(ids);
  };

  const handleAddPiece = async () => {
    if (!newPieceTypeId) return;
    const nextIds = [...pieceTypeIds, newPieceTypeId];
    try {
      await persistPieceTypeIds(nextIds);
      // A new piece usually shares the main garment's fabric (e.g. a co-ord bottom in the same
      // silk as the top) — pre-fill from it. The dropdowns save immediately, so overriding is one click.
      if (mainShellCompositionId || mainLiningCompositionId) {
        await savePieceComposition(newPieceTypeId, mainShellCompositionId, mainLiningCompositionId);
      }
      setActivePiece(newPieceTypeId);
      setNewPieceTypeId('');
      setAddingPiece(false);
    } catch (err) {
      toast.error(err.message || 'Failed to add piece');
    }
  };

  const handleRemovePiece = async (pieceKey) => {
    if (!window.confirm('Remove this piece and its composition?')) return;
    try {
      await api.del(`/api/admin/products/${productId}/piece-compositions/${pieceKey}`);
      const nextIds = pieceTypeIds.filter((id) => id !== pieceKey);
      await persistPieceTypeIds(nextIds);
      setPieceCompositions((prev) => {
        const next = { ...prev };
        delete next[pieceKey];
        return next;
      });
      if (activePiece === pieceKey) setActivePiece(MAIN_PIECE_KEY);
    } catch (err) {
      toast.error(err.message || 'Failed to remove piece');
    }
  };

  const saveMainCompositionChange = (field, compositionId) => {
    // Main piece composition lives on the product itself (composition_shell_id /
    // composition_lining_id) and is saved along with the rest of the product form.
    handleChange({ target: { name: field, value: compositionId } });

    if (field === 'composition_shell_id' && compositionId) {
      const composition = compositions.find((c) => c.id === compositionId);
      const catalogEntries = composition?.care_instructions_catalog || [];
      const careText =
        catalogEntries.length > 0
          ? catalogEntries.map((ci) => ci.instruction_text).join('. ')
          : composition?.care_instruction?.instruction_text || '';
      if (careText) {
        handleChange({ target: { name: 'care_instructions', value: careText } });
      }
    }
  };

  const savePieceComposition = async (pieceKey, shellId, liningId) => {
    try {
      await api.put(`/api/admin/products/${productId}/piece-compositions/${pieceKey}`, {
        shell_composition_id: shellId || null,
        lining_composition_id: liningId || null,
      });
      setPieceCompositions((prev) => ({
        ...prev,
        [pieceKey]: { shell_composition_id: shellId || null, lining_composition_id: liningId || null },
      }));
    } catch (err) {
      toast.error(err.message || 'Failed to save composition');
    }
  };

  if (!productId) {
    return (
      <div className="text-center py-12 text-muted-foreground/70 text-sm">
        Save the product first to set composition.
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <Layers size={20} />
        <h3 className="text-lg font-medium">Fabric Composition</h3>
      </div>

      <p className="text-xs text-muted-foreground mb-4">
        Set the fabric for the Main garment below. If different parts of this product use different fabric (e.g. a Silk
        top with a Linen bottom), add each part as its own piece — the same pieces used in Measurements.
      </p>

      {/* Piece tabs */}
      <div className="flex items-center flex-wrap gap-2 mb-4">
        {pieces.map((piece) => (
          <div
            key={piece.key}
            className={`flex items-center gap-1.5 pl-3 pr-1.5 py-1.5 rounded-full text-sm border ${
              activePiece === piece.key
                ? 'bg-primary text-primary-foreground border-foreground'
                : 'bg-card text-foreground/80 border-input hover:border-muted-foreground'
            }`}
          >
            <button type="button" onClick={() => setActivePiece(piece.key)}>
              {piece.label}
            </button>
            {piece.key !== MAIN_PIECE_KEY && (
              <button
                type="button"
                onClick={() => handleRemovePiece(piece.key)}
                className={
                  activePiece === piece.key
                    ? 'text-white/70 hover:text-white'
                    : 'text-muted-foreground/70 hover:text-foreground'
                }
                title="Remove piece"
              >
                <X size={14} />
              </button>
            )}
          </div>
        ))}

        {addingPiece ? (
          <div className="flex items-center gap-1.5">
            <select
              value={newPieceTypeId}
              onChange={(e) => setNewPieceTypeId(e.target.value)}
              className="px-2 py-1.5 border border-input rounded-lg text-sm focus:outline-none focus:border-foreground"
            >
              <option value="">Select piece type...</option>
              {availablePieceTypes.map((pt) => (
                <option key={pt.id} value={pt.id}>
                  {pt.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={handleAddPiece}
              disabled={!newPieceTypeId}
              className="px-2 py-1.5 bg-primary text-primary-foreground text-xs rounded-md disabled:opacity-50"
            >
              Add
            </button>
            <button
              type="button"
              onClick={() => {
                setAddingPiece(false);
                setNewPieceTypeId('');
              }}
              className="text-muted-foreground/70 hover:text-foreground"
            >
              <X size={16} />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setAddingPiece(true)}
            disabled={availablePieceTypes.length === 0}
            className="flex items-center gap-1 px-3 py-1.5 rounded-full text-sm border border-dashed border-input text-muted-foreground hover:border-muted-foreground hover:text-foreground disabled:opacity-40"
            title={
              availablePieceTypes.length === 0
                ? 'All product types already added'
                : 'Add another piece (e.g. Top, Bottom)'
            }
          >
            <Plus size={14} /> Add piece
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-8 text-muted-foreground/70 text-sm">Loading...</div>
      ) : activePiece === MAIN_PIECE_KEY ? (
        <CompositionFields
          compositions={compositions}
          shellId={mainShellCompositionId}
          liningId={mainLiningCompositionId}
          onShellChange={(id) => saveMainCompositionChange('composition_shell_id', id)}
          onLiningChange={(id) => saveMainCompositionChange('composition_lining_id', id)}
        />
      ) : (
        <CompositionFields
          compositions={compositions}
          shellId={pieceCompositions[activePiece]?.shell_composition_id}
          liningId={pieceCompositions[activePiece]?.lining_composition_id}
          onShellChange={(id) =>
            savePieceComposition(activePiece, id, pieceCompositions[activePiece]?.lining_composition_id)
          }
          onLiningChange={(id) =>
            savePieceComposition(activePiece, pieceCompositions[activePiece]?.shell_composition_id, id)
          }
        />
      )}
    </div>
  );
};

export default ProductCompositionManager;
